// src/shared/components/common/SearchForm/SearchForm.tsx
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Search, SlidersHorizontal, ChevronDown, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../../hooks/useTranslation';
import type { SearchFormProps } from './SearchForm.types';
import { useTheme } from '../../../../core/providers/ThemeProvider';
import { useResponsive } from '../../../utils/responsive';
import { Paper, Box, TextField, Select, MenuItem, IconButton } from '@mui/material';
import { formatDate } from './searchFormUtils';
import { convertToUTC, extractUTCDate } from '../../../utils/formatUtils';
import { getSearchCoordinates } from '../../../utils/locationService';
import { useUserStore } from '../../../../app/store/useUserStore';
import { SkyporterEvents } from '../../../../analytics/skyporterEvents';
import { useGetAirportsQuery } from '../../../../api/userApi';
import DropdownMenu from '../../ui/Menu/Dropdownmenu';
import DateRangePicker from '../../ui/Menu/Daterangepicker';
import { useSearchStore } from '../../../../app/store/useSearchStore';
import FilterDialog, {type FilterValues } from '../../ui/Dialog/FilterDialog';
import { useAuth0 } from '@auth0/auth0-react';
import { useDebounce } from '../../../hooks/useDebounce';

export const SearchForm: React.FC<SearchFormProps> = ({
  onSearch,
  initialValues,
  className,
  style,
  isHomePage = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { theme, userType } = useTheme();
  const { isMobile, isTablet, isLargeDesktop, isXLarge } = useResponsive();
  const { isAuthenticated } = useAuth0();

  // Input refs for dropdown positioning
  const fromInputRef = useRef<HTMLDivElement | null>(null);
  const toInputRef = useRef<HTMLDivElement | null>(null);
  const datePickerRef = useRef<HTMLDivElement | null>(null);
  const formRef = useRef<HTMLDivElement | null>(null);

  // Prevents a second click from firing a duplicate submit while the first one
  // is still awaiting async work (e.g. the browser's geolocation permission prompt)
  const [isSearching, setIsSearching] = useState(false);

  // Get state and actions from Zustand store
  const {
    from,
    to,
    startDate,
    endDate,
    fromSearch,
    toSearch,
    errors,
    fromDropdownOpen,
    toDropdownOpen,
    datePickerOpen,
    filterDialogOpen,
    formSubmitted,
    filters,
    searchForRole,
    searchPayload,
    setFrom,
    setTo,
    setDates,
    setFromSearch,
    setToSearch,
    setFromDropdownOpen,
    setToDropdownOpen,
    setDatePickerOpen,
    setFilterDialogOpen,
    setFilters,
    setError,
    setFormSubmitted,
    setUserRole,
    setSearchForRole,
    setSearchPayload,
  } = useSearchStore();

  // Sync userType from theme context with searchStore whenever it changes
  useEffect(() => {
    if (userType) {
      setUserRole(userType);
    }
  }, [userType, setUserRole]);

  // Initialize store with props if provided
  useEffect(() => {
    if (initialValues) {
      if (initialValues.from) {
        const fromIata = (initialValues.from as any).iata || initialValues.from.code;
        setFrom(initialValues.from);
        const fromSearchValue = `${initialValues.from.name}${fromIata ? ` (${fromIata})` : ''}, ${initialValues.from.city}, ${initialValues.from.country_name}`;
        setFromSearch(fromSearchValue);
      }

      if (initialValues.to) {
        const toIata = (initialValues.to as any).iata || initialValues.to.code;
        setTo(initialValues.to);
        const toSearchValue = `${initialValues.to.name}${toIata ? ` (${toIata})` : ''}, ${initialValues.to.city}, ${initialValues.to.country_name}`;
        setToSearch(toSearchValue);
      }

      if (initialValues.startDate && initialValues.endDate) {
        setDates(
          new Date(initialValues.startDate),
          new Date(initialValues.endDate)
        );
      }
    }
  }, [initialValues]);

  // Airport lookups wait until the user pauses typing
  const debouncedFromSearch = useDebounce(fromSearch, 500);
  const debouncedToSearch = useDebounce(toSearch, 500);
  const { data: fromAirports = [], isLoading: isFromLoading } =
    useGetAirportsQuery(debouncedFromSearch, { skip: debouncedFromSearch.length < 2 });

  const { data: toAirports = [], isLoading: isToLoading } =
    useGetAirportsQuery(debouncedToSearch, { skip: debouncedToSearch.length < 2 });

  // Add click outside handler to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Get the clicked element
      const clickedElement = event.target as Node;

      // Check if clicked outside of each input/dropdown
      const isOutsideFromInput = fromInputRef.current &&
        !fromInputRef.current.contains(clickedElement);

      const isOutsideToInput = toInputRef.current &&
        !toInputRef.current.contains(clickedElement);

      const isOutsideDatePicker = datePickerRef.current &&
        !datePickerRef.current.contains(clickedElement);

      // Close each dropdown only if it's open and the click was outside its area
      if (fromDropdownOpen && isOutsideFromInput) {
        setFromDropdownOpen(false);
      }

      if (toDropdownOpen && isOutsideToInput) {
        setToDropdownOpen(false);
      }

      if (datePickerOpen && isOutsideDatePicker) {
        setDatePickerOpen(false);
      }
    };

    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);

    // Clean up
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [fromDropdownOpen, toDropdownOpen, datePickerOpen]);

  // Prepare dropdown items with IATA codes
  const fromDropdownItems = fromAirports.map((airport) => {
    const iataCode = (airport as any).iata || airport.code;
    return {
      id: airport.id.toString(),
      primaryText: `${airport.name}${iataCode ? ` (${iataCode})` : ''}`,
      secondaryText: `${airport.city}, ${airport.country_name}`,
      icon: 'airport'
    };
  });

  const toDropdownItems = toAirports.map((airport) => {
    const iataCode = (airport as any).iata || airport.code;
    return {
      id: airport.id.toString(),
      primaryText: `${airport.name}${iataCode ? ` (${iataCode})` : ''}`,
      secondaryText: `${airport.city}, ${airport.country_name}`,
      icon: 'airport'
    };
  });

  // Handle field changes
  const handleFromSelect = (item: unknown) => {
    const airport = fromAirports.find(a => a.id.toString() === item.id);
    if (airport) {
      const iataCode = (airport as any).iata || airport.code;
      setFrom(airport);
      setFromSearch(
        `${airport.name}${iataCode ? ` (${iataCode})` : ''}, ${airport.city}, ${airport.country_name}`
      );
      setFromDropdownOpen(false);
    }
  };

  const handleToSelect = (item: unknown) => {
    const airport = toAirports.find(a => a.id.toString() === item.id);
    if (airport) {
      const iataCode = (airport as any).iata || airport.code;
      setTo(airport);
      setToSearch(
        `${airport.name}${iataCode ? ` (${iataCode})` : ''}, ${airport.city}, ${airport.country_name}`
      );
      setToDropdownOpen(false);
    }
  };

  const handleDateRangeChange = (value: [Date | null, Date | null]) => {
    setDates(value[0], value[1]);
    if (!value[0] && !value[1] && searchPayload) {
      const updated = { ...searchPayload, startDate: '', endDate: '' };
      const allCleared = !updated.fromLocation && !updated.toLocation && !updated.startDate && !updated.endDate;
      setSearchPayload(allCleared ? null : updated);
    }
  };

  // Function to open the date picker and close other dropdowns
  const handleDatePickerOpen = () => {
    setDatePickerOpen(true);
    setFromDropdownOpen(false);
    setToDropdownOpen(false);
  };

  // Function to focus From input and show dropdown if it has content
  const handleFromInputClick = () => {
    if (fromSearch.length >= 2) {
      setFromDropdownOpen(true);
      setToDropdownOpen(false);
      setDatePickerOpen(false);
    }
  };

  // Function to focus To input and show dropdown if it has content
  const handleToInputClick = () => {
    if (toSearch.length >= 2) {
      setToDropdownOpen(true);
      setFromDropdownOpen(false);
      setDatePickerOpen(false);
    }
  };

  // Function to clear From input
  const handleClearFrom = () => {
    setFrom(null);
    setFromSearch('');
    setFromDropdownOpen(false);
    setError('from', false);
    if (searchPayload) {
      const updated = { ...searchPayload, fromLocation: null };
      const allCleared = !updated.fromLocation && !updated.toLocation && !updated.startDate && !updated.endDate;
      setSearchPayload(allCleared ? null : updated);
    } else {
      setSearchPayload(null);
    }
    navigate('/', { state: null });
  };

  // Function to clear To input
  const handleClearTo = () => {
    setTo(null);
    setToSearch('');
    setToDropdownOpen(false);
    setError('to', false);
    if (searchPayload) {
      const updated = { ...searchPayload, toLocation: null };
      const allCleared = !updated.fromLocation && !updated.toLocation && !updated.startDate && !updated.endDate;
      setSearchPayload(allCleared ? null : updated);
    } else {
      setSearchPayload(null);
    }
    navigate('/', { state: null });
  };

  // Form validation and submission
  const validateForm = (): boolean => {
    // Validate from field (required) - trim whitespace
    const trimmedFromSearch = fromSearch.trim();
    const hasFromError = !from || typeof from !== 'object' || !trimmedFromSearch;
    setError('from', hasFromError);

    // Validate to field (required) - trim whitespace
    const trimmedToSearch = toSearch.trim();
    const hasToError = !to || typeof to !== 'object' || !trimmedToSearch;
    setError('to', hasToError);

    // Validate date range (required)
    const hasDateError = !startDate || !endDate;
    setError('dateRange', hasDateError);

    return !hasFromError && !hasToError && !hasDateError;
  };

  // Handle filter apply
  const handleApplyFilters = (filterValues: FilterValues) => {
    setFilters({
      radius: filterValues.radius,
      minWeight: filterValues.minWeight,
      maxWeight: filterValues.maxWeight,
      weightUnit: filterValues.weightUnit,
      sortBy: filterValues.sortBy,
    });
  };

  const handleSubmit = async () => {
    // A second click while the first is still awaiting async work (e.g. the
    // browser's geolocation permission prompt) must not fire a duplicate submit
    if (isSearching) {
      return;
    }

    setFormSubmitted(true);

    // If both airports are cleared, navigate to default search instead of validating
    if (!from && !to && !fromSearch.trim() && !toSearch.trim()) {
      setSearchPayload(null);
      navigate('/', { state: null });
      return;
    }

    // Validate form
    if (!validateForm()) {
      return;
    }

    setIsSearching(true);

    try {
      // Get search parameters
      const fromId = from?.id;
      const toId = to?.id;

      // Format dates - they are optional, so empty strings are valid
      const startDateStr = formatDate(startDate);
      const endDateStr = formatDate(endDate);

      // Convert start date to UTC using the current local time (not midnight) before sending to the API
      const now = new Date();
      const currentTimeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
      const startDateUTCIso = startDateStr ? convertToUTC(startDateStr, currentTimeStr) : null;
      const startDateUTC = startDateUTCIso ? extractUTCDate(startDateUTCIso) : '';

      if (!fromId) {
        setError('from', true);
        return;
      }

      if (!toId) {
        setError('to', true);
        return;
      }

      // Get user location: browser geolocation → profile address → timezone
      // Location resolution must not block the search if all tiers fail
      const zustandUser = useUserStore.getState().user;
      let latitude: number | undefined;
      let longitude: number | undefined;
      try {
        const coords = await getSearchCoordinates(zustandUser);
        latitude = coords.latitude;
        longitude = coords.longitude;
      } catch {
        // Could not resolve location - proceed with search without coordinates
      }

      // Create search payload with filters
      const payload: unknown = {
        fromLocation: fromId,
        toLocation: toId,
        startDate: startDateUTC,
        endDate: endDateStr,
        latitude,
        longitude,
        radius: filters.radius || 500,
        page: 1,
        limit: 10,
        sortBy: filters.sortBy || undefined,
        min_weight: filters.minWeight && filters.minWeight > 0 ? filters.minWeight : undefined,
        max_weight: filters.maxWeight && filters.maxWeight > 0 ? filters.maxWeight : undefined,
        weightUnit: filters.weightUnit || "lb",
      };

      const effectiveUserRole = isAuthenticated ? userType : (searchForRole === 'sender' ? 'traveller' : 'sender');
      const zustandUserId = useUserStore.getState().user?.user_id ?? '';
      const originName = (from as any)?.name ?? String(fromId);
      const destinationName = (to as any)?.name ?? String(toId);
      const dateRange = `${startDateStr}-${endDateStr}`;

      if (effectiveUserRole !== 'sender') {
        SkyporterEvents.itemSearchPerformed({ userId: zustandUserId, origin: originName, destination: destinationName, dateRange });
      } else {
        SkyporterEvents.itinerarySearchPerformed({ userId: zustandUserId, origin: originName, destination: destinationName, dateRange });
      }

      if (isHomePage) {
        // For unauthenticated users, use searchForRole to determine which API to call
        // If user selects "Sender", they are a traveller searching for senders
        // If user selects "Traveller", they are a sender searching for travellers

        navigate("/", {
          state: {
            searchPayload: payload,
            userRole: effectiveUserRole,
            // For unauthenticated users, pass 'generic' for UI theme
            themeRole: isAuthenticated ? userType : 'generic',
            fromPath: window.location.pathname // Pass current path for fallback
          }
        });
      } else if (onSearch) {
        onSearch(payload, effectiveUserRole);
      }
    } catch {
      setError('from', true);
    } finally {
      setIsSearching(false);
    }
  };

  // Get font family from theme
  const fontFamily = theme.typography.bodyText.fontFamily as string;

  // Responsive sizing with tablet support
  const basePadding = isMobile ? '16px' : isTablet ? '20px' : '24px';
  const buttonSize = isMobile ? 56 : isTablet ? 60 : 64;
  const borderRadius = isMobile ? '40px' : isTablet ? '55px' : '70px';

  return (
    <>
    <Paper
      elevation={4}
      className={className}
      ref={formRef}
      sx={{
        borderRadius,
        padding: 0, // Remove padding to achieve seamless look
        width: '100%',
        maxWidth: isMobile ? '100%' : isTablet ? '95%' : '1200px',
        margin: '0 auto',
        backgroundColor: '#FFFFFF',
        boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
        display: 'flex',
        flexDirection: isMobile ? 'column' : isTablet ? 'column' : 'row',
        alignItems: 'center',
        gap: 0,
        position: 'relative', // Important for dropdown positioning
        overflow: 'visible', // Allow dropdowns to be visible outside paper
        ...style
      }}
    >
      {/* Search For field - Only show for unauthenticated users */}
      {!isAuthenticated && (
        <Box
          sx={{
            flex: isMobile ? 'none' : isTablet ? 'none' : 0.8,
            width: isMobile ? '100%' : isTablet ? '100%' : 'auto',
            minWidth: 0,
            position: 'relative',
            px: basePadding,
            py: isMobile ? '12px' : isTablet ? '14px' : '16px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
            }}
          >
            <label
              style={{
                fontSize: '12px',
                fontWeight: 500,
                color: '#222222',
                fontFamily,
              }}
            >
              Search For
            </label>
            <Select
              value={searchForRole}
              onChange={(e) => setSearchForRole(e.target.value as 'sender' | 'traveller')}
              sx={{
                fontSize: '14px',
                fontWeight: 400,
                fontFamily,
                color: '#222222',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '& .MuiSelect-select': {
                  padding: '0',
                  paddingRight: '24px !important',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '& .MuiSelect-icon': {
                  right: 0,
                  color: '#222222',
                },
              }}
            >
              <MenuItem value="sender" sx={{ fontFamily, fontSize: '14px' }}>{t('searchForm.senderOption')}</MenuItem>
              <MenuItem value="traveller" sx={{ fontFamily, fontSize: '14px' }}>{t('searchForm.travelerOption')}</MenuItem>
            </Select>
          </Box>
        </Box>
      )}

      {/* From field */}
      <Box
        sx={{
          flex: isMobile ? 'none' : isTablet ? 'none' : 1,
          width: isMobile ? '100%' : isTablet ? '100%' : 'auto',
          minWidth: 0,
          position: 'relative',
          px: basePadding,
          py: isMobile ? '12px' : isTablet ? '14px' : '16px',
          borderLeft: !isAuthenticated && !isMobile && !isTablet ? '1px solid rgba(0, 0, 0, 0.1)' : 'none',
          borderTop: !isAuthenticated && (isMobile || isTablet) ? '1px solid rgba(0, 0, 0, 0.1)' : 'none',
        }}
        ref={fromInputRef}
      >
        <TextField
          label={t('searchForm.fromLabel')}
          value={fromSearch}
          onChange={(e) => setFromSearch(e.target.value)}
          onClick={handleFromInputClick}
          onFocus={handleFromInputClick}
          placeholder={t('searchForm.cityOrAirportPlaceholder')}
          fullWidth
          error={formSubmitted && errors.from}
          helperText={formSubmitted && errors.from ? 'Please select a valid airport' : ''}
          sx={{
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: theme.colors.primary,
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.colors.primary,
              },
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none', // Remove border
            },
            '& .MuiInputBase-root': {
              fontSize: '14px',
              fontWeight: 400,
              fontFamily,
              padding: 0,
              paddingRight: fromSearch ? '24px' : 0,
              color: '#222222',
            },
            '& .MuiInputBase-input': {
              padding: '0',
              '&::placeholder': {
                color: '#6A6A6A',
                opacity: 1,
                fontSize: '14px',
                fontWeight: 400,
              },
            },
            '& .MuiFormHelperText-root': {
              fontSize: { xs: "0.6rem", sm: "0.7rem" },
              position: "absolute",
              bottom: { xs: -16, sm: -20 },
              margin: 0,
            },
            '& .MuiInputLabel-root': {
              position: 'relative',
              transform: 'none',
              fontSize: '12px',
              fontWeight: 500,
              color: '#222222',
              marginBottom: '4px',
              '&.Mui-focused': {
                color: '#222222',
              },
            },
            '& .MuiInputLabel-shrink': {
              transform: 'none',
            },
          }}
          slotProps={{
            inputLabel: {
              shrink: true,
            },
            input: {
              endAdornment: fromSearch ? (
                <IconButton
                  onClick={handleClearFrom}
                  size="small"
                  sx={{
                    padding: '2px',
                    position: 'absolute',
                    right: 0,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <X size={16} color="#6A6A6A" />
                </IconButton>
              ) : null,
            },
          }}
          variant="outlined"
        />

        <DropdownMenu
          items={fromDropdownItems}
          onSelect={handleFromSelect}
          selectedItem={from?.id.toString()}
          open={fromDropdownOpen}
          anchorEl={fromInputRef.current}
          onClose={() => setFromDropdownOpen(false)}
          width={isMobile ? '90vw' : 388}
        />
      </Box>

      {/* To field */}
      <Box
        sx={{
          flex: isMobile ? 'none' : isTablet ? 'none' : 1,
          width: isMobile ? '100%' : isTablet ? '100%' : 'auto',
          minWidth: 0,
          position: 'relative',
          px: basePadding,
          py: isMobile ? '12px' : isTablet ? '14px' : '16px',
          borderLeft: isMobile || isTablet ? 'none' : '1px solid rgba(0, 0, 0, 0.1)',
          borderTop: isMobile || isTablet ? '1px solid rgba(0, 0, 0, 0.1)' : 'none',
        }}
        ref={toInputRef}
      >
        <TextField
          label={t('searchForm.toLabel')}
          value={toSearch}
          onChange={(e) => setToSearch(e.target.value)}
          onClick={handleToInputClick}
          onFocus={handleToInputClick}
          placeholder={t('searchForm.cityOrAirportPlaceholder')}
          fullWidth
          error={formSubmitted && errors.to}
          helperText={formSubmitted && errors.to ? 'Please select a destination' : ''}
          sx={{
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: theme.colors.primary,
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.colors.primary,
              },
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none', // Remove border
            },
            '& .MuiInputBase-root': {
              fontSize: '14px',
              fontWeight: 400,
              fontFamily,
              padding: 0,
              paddingRight: toSearch ? '24px' : 0,
              color: '#222222',
            },
            '& .MuiInputBase-input': {
              padding: '0',
              '&::placeholder': {
                color: '#6A6A6A',
                opacity: 1,
                fontSize: '14px',
                fontWeight: 400,
              },
            },
            '& .MuiFormHelperText-root': {
              fontSize: { xs: "0.6rem", sm: "0.7rem" },
              position: "absolute",
              bottom: { xs: -16, sm: -20 },
              margin: 0,
            },
            '& .MuiInputLabel-root': {
              position: 'relative',
              transform: 'none',
              fontSize: '12px',
              fontWeight: 500,
              color: '#222222',
              marginBottom: '4px',
              '&.Mui-focused': {
                color: '#222222',
              },
            },
            '& .MuiInputLabel-shrink': {
              transform: 'none',
            },
          }}
          slotProps={{
            inputLabel: {
              shrink: true,
            },
            input: {
              endAdornment: toSearch ? (
                <IconButton
                  onClick={handleClearTo}
                  size="small"
                  sx={{
                    padding: '2px',
                    position: 'absolute',
                    right: 0,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <X size={16} color="#6A6A6A" />
                </IconButton>
              ) : null,
            },
          }}
          variant="outlined"
        />

        <DropdownMenu
          items={toDropdownItems}
          onSelect={handleToSelect}
          selectedItem={to?.id.toString()}
          open={toDropdownOpen}
          anchorEl={toInputRef.current}
          onClose={() => setToDropdownOpen(false)}
          width={isMobile ? '90vw' : 388}
        />
      </Box>

      {/* Date Range */}
      <Box
        sx={{
          flex: isMobile ? 'none' : isTablet ? 'none' : 1.5,
          width: isMobile ? '100%' : isTablet ? '100%' : 'auto',
          minWidth: 0,
          px: basePadding,
          py: isMobile ? '12px' : isTablet ? '14px' : '16px',
          borderLeft: isMobile || isTablet ? 'none' : '1px solid rgba(0, 0, 0, 0.1)',
          borderTop: isMobile || isTablet ? '1px solid rgba(0, 0, 0, 0.1)' : 'none',
          position: 'relative',
        }}
        ref={datePickerRef}
      >
        <DateRangePicker
          value={[startDate, endDate]}
          onChange={handleDateRangeChange}
          label="Date Range"
          placeholder={t('searchForm.dateRangePlaceholder')}
          fullWidth
          userType="traveller"
          open={datePickerOpen}
          onOpen={() => setDatePickerOpen(true)}
          onClose={() => setDatePickerOpen(false)}
          hideIcon={true}
          showClearButton={true}
          error={formSubmitted && errors.dateRange}
          helperText={formSubmitted && errors.dateRange ? 'Please select a date range' : ''}
          sx={{
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: theme.colors.primary,
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.colors.primary,
              },
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '& .MuiInputBase-root': {
              padding: 0,
              fontSize: '14px',
              fontWeight: 400,
              fontFamily,
              color: '#222222',
            },
            '& .MuiInputBase-input': {
              padding: '0',
              '&::placeholder': {
                color: '#6A6A6A',
                opacity: 1,
                fontSize: '14px',
                fontWeight: 400,
              },
            },
            '& .MuiInputLabel-root': {
              position: 'relative',
              transform: 'none',
              fontSize: '12px',
              fontWeight: 500,
              color: '#222222',
              marginBottom: '4px',
              '&.Mui-focused': {
                color: '#222222',
              },
            },
            '& .MuiInputLabel-shrink': {
              transform: 'none',
            },
            '& .MuiInputAdornment-root': {
              margin: '0 0 0 8px',
              height: '100%',
            }
          }}
        />
      </Box>

      {/* Filter and Search buttons */}
      <Box
        sx={{
          padding: isMobile ? basePadding : isTablet ? basePadding : '12px 24px 12px 0',
          display: 'flex',
          gap: isMobile ? '12px' : isTablet ? '12px' : '8px',
          justifyContent: isMobile ? 'center' : isTablet ? 'center' : 'flex-end',
          width: isMobile ? '100%' : isTablet ? '100%' : 'auto',
          borderTop: isMobile || isTablet ? '1px solid rgba(0, 0, 0, 0.1)' : 'none',
        }}
      >
        {/* Search button */}
        <button
          onClick={handleSubmit}
          disabled={isSearching}
          style={{
            width: isMobile ? 'calc(100% - 60px)' : isTablet ? 'calc(100% - 68px)' : `${buttonSize}px`,
            height: `${buttonSize}px`,
            borderRadius: isMobile ? '12px' : isTablet ? '12px' : '50%',
            backgroundColor: '#1F2937',
            border: 'none',
            cursor: isSearching ? 'not-allowed' : 'pointer',
            opacity: isSearching ? 0.7 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transition: 'all 150ms ease-in-out',
            transform: 'scale(1)',
          }}
          onMouseEnter={(e) => {
            if (isSearching) return;
            e.currentTarget.style.backgroundColor = '#374151';
            e.currentTarget.style.transform = 'scale(1.02)';
          }}
          onMouseLeave={(e) => {
            if (isSearching) return;
            e.currentTarget.style.backgroundColor = '#1F2937';
            e.currentTarget.style.transform = 'scale(1)';
          }}
          onMouseDown={(e) => {
            if (isSearching) return;
            e.currentTarget.style.transform = 'scale(0.95)';
          }}
          onMouseUp={(e) => {
            if (isSearching) return;
            e.currentTarget.style.transform = 'scale(1.02)';
          }}
          aria-label={t('searchForm.searchButton')}
          type="button"
        >
          <Search size={isMobile ? 22 : isTablet ? 23 : 24} color="#FFFFFF" />
          {(isMobile || isTablet) && (
            <span style={{
              color: '#FFFFFF',
              fontFamily,
              marginLeft: '8px',
              fontSize: '14px',
              fontWeight: 500
            }}>
              Search
            </span>
          )}
        </button>
      </Box>

    </Paper>

    {/* Filter Dialog - Rendered outside Paper to avoid z-index issues */}
    <FilterDialog
      open={filterDialogOpen}
      onClose={() => setFilterDialogOpen(false)}
      onApply={handleApplyFilters}
      initialFilters={filters}
    />
  </>
  );
};